import { useCallback, useEffect, useRef, useState } from 'react'
import { governanceApi, type MerchantAnswerItem } from '../../services/api'
import {
  Alert,
  Button,
  Card,
  CardBody,
  CardFooter,
  CardHeader,
  CardList,
  EmptyState,
  ErrorState,
  Field,
  Hint,
  LoadingState,
} from '../../ds'

/**
 * Knowledge → Your answers (Knowledge v6, MAR-325): the answers the merchant has given,
 * which Marva reuses so the same question never reaches them twice. Edit, expiry, remove.
 *
 * MOVED from `components/setup/NotificationsSection.tsx` ("Store knowledge"), with every
 * MAR-323 state intact. There is one home for it now; the Notifications section keeps only
 * delivery preferences.
 *
 * ── The read is split from the editor ───────────────────────────────────────────────────
 *
 * `useMerchantAnswers` is called by the PAGE, once, so the "Your answers" scope chip can
 * print an exact count — and only when the read was complete. The editor is handed the
 * read rather than making its own, so the chip and the list can never be two reads that
 * disagree. The page defers the read until the Your answers scope is open (`enabled`), so
 * the chip carries no count before then — absent, never a guess.
 *
 * ── The four states ─────────────────────────────────────────────────────────────────────
 *
 * - `idle` — no workspace, or not yet enabled, so no read was ever issued. It renders NONE of the states below:
 *   not 'loading' (nothing is in flight, so it would spin forever), and above all not
 *   "Nothing yet", which is a claim about a store nobody read.
 * - `loading`.
 * - `failed` — NOT 'loaded with nothing': the answers API returns a 500
 *   `merchant_answers_unavailable` on a failed read precisely so this can tell "you have no
 *   answers" from "we could not read them". Collapsing the two would tell a merchant their
 *   answers are gone. A retry is offered.
 * - `loaded` — with `exhaustive: false` meaning the store held more than one page (absent
 *   reads as complete; older API responses never sent the flag). Above its cap the read
 *   returns an ARBITRARY subset, so the copy claims a count and a gap, never an order.
 *
 * ⚠️ One stored read, NAMING the workspace it was requested for, scoped at the read: a value
 * from another workspace renders as 'loading', never as this workspace's answers. Without
 * that, a slow read for the workspace you just left lands after the switch and shows its
 * answers (or its failure) under the new one.
 */

export type AnswersState = 'idle' | 'loading' | 'failed' | 'loaded'

interface AnswersRead {
  workspaceId: string
  state: 'loading' | 'failed' | 'loaded'
  answers: MerchantAnswerItem[]
  exhaustive: boolean
}

export interface MerchantAnswersHandle {
  workspaceId: string | undefined
  state: AnswersState
  answers: MerchantAnswerItem[]
  exhaustive: boolean
  reload: () => void
  /** Apply a local edit to THIS workspace's list. A no-op against another workspace's read. */
  update: (fn: (list: MerchantAnswerItem[]) => MerchantAnswerItem[]) => void
}

/** The exact number of answers, or `null` when the read was not complete. */
export function exactAnswerCount(h: MerchantAnswersHandle): number | null {
  return h.state === 'loaded' && h.exhaustive ? h.answers.length : null
}

export function useMerchantAnswers(workspaceId: string | undefined, enabled = true): MerchantAnswersHandle {
  const [read, setRead] = useState<AnswersRead | null>(null)
  // Only the newest request may write: a retry overlapping a slow first read, or a
  // workspace switch, supersedes the earlier one.
  const request = useRef(0)
  /** Read once per workspace: leaving the scope and coming back keeps the list and its edits. */
  const requested = useRef<string | undefined>(undefined)

  const load = useCallback(async() => {
    if (!workspaceId) return
    const mine = ++request.current
    const write = (state: 'failed' | 'loaded', answers: MerchantAnswerItem[], exhaustive = true) => {
      if (mine === request.current) setRead({ workspaceId, state, answers, exhaustive })
    }
    setRead({ workspaceId, state: 'loading', answers: [], exhaustive: true })
    try {
      const res = await governanceApi.getMerchantAnswers(workspaceId)
      if (!res.success || !res.data) throw new Error('merchant_answers_unavailable')
      write('loaded', res.data.answers || [], res.data.exhaustive !== false)
    } catch {
      // ⚠️ Never fall through to an empty list: that renders "Nothing yet", which is a
      // claim about the store this read could not make.
      write('failed', [])
    }
  }, [workspaceId])

  useEffect(() => {
    if (!workspaceId || !enabled || requested.current === workspaceId) return
    requested.current = workspaceId
    void load()
  }, [load, workspaceId, enabled])

  const current = read && read.workspaceId === workspaceId ? read : null
  const state: AnswersState = !workspaceId || (!current && !enabled) ? 'idle' : (current?.state ?? 'loading')
  const update = useCallback(
    (fn: (list: MerchantAnswerItem[]) => MerchantAnswerItem[]) =>
      setRead((r) => (r && r.workspaceId === workspaceId ? { ...r, answers: fn(r.answers) } : r)),
    [workspaceId]
  )
  return {
    workspaceId,
    state,
    answers: current?.answers ?? [],
    exhaustive: current?.exhaustive ?? true,
    reload: () => {
      void load()
    },
    update,
  }
}

const pad = (n: number) => String(n).padStart(2, '0')

/** A `Date`'s LOCAL calendar day as `YYYY-MM-DD` — the value a date input holds. */
export function localDay(d: Date): string {
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`
}

/**
 * The stored instant → the merchant's LOCAL day it falls on, for the input and the
 * "expires …" line alike. ⚠️ Never `iso.slice(0, 10)`: that is the UTC day, which for a
 * merchant west of UTC is the day AFTER the one they picked (local 23:59 is tomorrow in UTC).
 */
export function dateOf(iso: string | null | undefined): string {
  if (!iso) return ''
  const d = new Date(iso)
  return isNaN(d.getTime()) ? '' : localDay(d)
}

/**
 * The stored instant as the merchant reads a day everywhere else in the app ("24 Oct 2026",
 * MAR-412) — the same LOCAL day `dateOf` gives the input, so the line and the input agree.
 */
export function dayText(iso: string | null | undefined): string {
  if (!iso) return ''
  const d = new Date(iso)
  return isNaN(d.getTime()) ? '' : d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })
}

/**
 * A date input's value back to the ISO instant the store compares against. The read treats
 * an answer as live while `expiresAt > now`, so the END of the chosen day — the merchant's
 * local day, the one they picked — is when it stops being reused. Empty clears the expiry
 * (`null`), which the API reads as "never expires".
 */
export function expiryFromDate(d: string): string | null {
  if (!d) return null
  const [y, m, day] = d.split('-').map(Number)
  if (!y || !m || !day) return null
  return new Date(y, m - 1, day, 23, 59, 59, 999).toISOString()
}

interface Draft {
  answerText?: string
  expiry?: string
}

type AnswerPatch = { answerText?: string; expiresAt?: string | null }

/**
 * What a save would send, from the SAME normalisation the save uses: the trimmed draft
 * against the trimmed stored text, and the chosen day. `dirty` is `Object.keys(patch).length > 0`, so a whitespace-only edit —
 * which trims back to the stored answer — leaves Save disabled, and `save` refuses an empty
 * patch rather than sending a PATCH that changes nothing.
 */
export function answerPatch(item: MerchantAnswerItem, d: Draft): AnswerPatch {
  const patch: AnswerPatch = {}
  const text = (d.answerText ?? item.answerText).trim()
  // Both sides normalised: a stored answer carrying stray whitespace is not "changed" by
  // being left alone, or by an edit that trims to the same words.
  if (text !== item.answerText.trim()) patch.answerText = text
  if (d.expiry !== undefined && d.expiry !== dateOf(item.expiresAt)) patch.expiresAt = expiryFromDate(d.expiry)
  return patch
}

export default function MerchantAnswers({ read, query = '' }: { read: MerchantAnswersHandle; query?: string }) {
  const { workspaceId, state, answers, exhaustive } = read
  /**
   * ⚠️ Drafts and the message NAME the workspace they were made in and are matched at the
   * READ, as the answers read is: an unsaved edit or "Knowledge entry saved." from one
   * workspace must never render under another (fingerprints are per-question hashes, so the
   * same question in two stores would otherwise carry one store's draft into the other).
   */
  const [draftStore, setDraftStore] = useState<{ workspaceId: string | undefined; drafts: Record<string, Draft> }>({
    workspaceId,
    drafts: {},
  })
  const drafts = draftStore.workspaceId === workspaceId ? draftStore.drafts : {}
  const setDrafts = (fn: (all: Record<string, Draft>) => Record<string, Draft>) =>
    setDraftStore((cur) => ({ workspaceId, drafts: fn(cur.workspaceId === workspaceId ? cur.drafts : {}) }))
  const [saving, setSaving] = useState<string | null>(null)
  const [messageStore, setMessageStore] = useState<{ workspaceId: string | undefined; text: string; bad: boolean } | null>(null)
  const message = messageStore && messageStore.workspaceId === workspaceId ? messageStore : null
  /** Stamped with the workspace the ACT was for, not the one live when it resolves. */
  const say = (ws: string | undefined, text: string, bad: boolean) => setMessageStore({ workspaceId: ws, text, bad })
  const today = localDay(new Date())

  const q = query.trim().toLowerCase()
  const shown = q
    ? answers.filter((a) => `${a.question} ${a.answerText}`.toLowerCase().includes(q))
    : answers

  const save = async(item: MerchantAnswerItem) => {
    if (!workspaceId) return
    const ws = workspaceId
    const d = drafts[item.fingerprint] || {}
    if (!(d.answerText ?? item.answerText).trim()) {
      say(workspaceId, 'The answer cannot be empty — remove the entry instead.', true)
      return
    }
    const patch = answerPatch(item, d)
    // Defence in depth: Save is disabled for this, but a save that would change nothing
    // must never reach the server as a PATCH.
    if (Object.keys(patch).length === 0) return
    try {
      setSaving(item.fingerprint)
      const res = await governanceApi.updateMerchantAnswer(workspaceId, item.fingerprint, patch)
      if (!res.success) throw new Error(res.error || 'Could not update the answer')
      read.update((list) =>
        list.map((k) =>
          k.fingerprint === item.fingerprint
            ? { ...k, ...(patch.answerText !== undefined ? { answerText: patch.answerText } : {}), ...('expiresAt' in patch ? { expiresAt: patch.expiresAt } : {}) }
            : k
        )
      )
      // Cleared in the workspace the save was FOR; a save that lands after a switch
      // must not reach into the other workspace's drafts.
      setDraftStore((cur) => {
        if (cur.workspaceId !== ws) return cur
        const { [item.fingerprint]: _gone, ...rest } = cur.drafts
        void _gone
        return { workspaceId: ws, drafts: rest }
      })
      say(workspaceId, 'Knowledge entry saved.', false)
    } catch (e) {
      say(workspaceId, e instanceof Error ? e.message : 'Could not update the answer', true)
    } finally {
      setSaving(null)
    }
  }

  const remove = async(item: MerchantAnswerItem) => {
    if (!workspaceId) return
    try {
      setSaving(item.fingerprint)
      const res = await governanceApi.deleteMerchantAnswer(workspaceId, item.fingerprint)
      if (!res.success) throw new Error(res.error || 'Could not remove the entry')
      read.update((list) => list.filter((k) => k.fingerprint !== item.fingerprint))
    } catch (e) {
      say(workspaceId, e instanceof Error ? e.message : 'Could not remove the entry', true)
    } finally {
      setSaving(null)
    }
  }

  return (
    <div data-testid="kn-answers">
      <p>Answers you’ve given. She reuses these so the same question never reaches you twice.</p>
      {state === 'loading' && <LoadingState caption="Loading your answers…" />}
      {state === 'failed' && (
        <ErrorState title="Couldn’t load your answers. Nothing has been removed — try again." onRetry={read.reload} />
      )}
      {/* ⚠️ Not "the first N": above its cap the read returns an ARBITRARY subset. */}
      {state === 'loaded' && !exhaustive && (
        <Hint>Showing {answers.length} of your answers — some aren’t shown.</Hint>
      )}
      {state === 'loaded' && exhaustive && answers.length === 0 && (
        <EmptyState size="sm">Nothing yet — entries appear here as you answer customer questions.</EmptyState>
      )}
      {state === 'loaded' && answers.length > 0 && q && shown.length === 0 && (
        <EmptyState size="sm">
          None of {exhaustive ? 'your' : 'the'} answers shown here mention “{query.trim()}”.
        </EmptyState>
      )}
      {state === 'loaded' && shown.length > 0 && (
        <CardList>
          {shown.map((item) => {
            const d = drafts[item.fingerprint] || {}
            const text = d.answerText ?? item.answerText
            const expiry = d.expiry ?? dateOf(item.expiresAt)
            const dirty = Object.keys(answerPatch(item, d)).length > 0
            return (
              <Card padding="sm" key={item.fingerprint} data-testid="kn-answer">
                <CardHeader
                  titleAs="p"
                  title={<>“{item.question}”</>}
                  description={
                    <>
                      Answered {dayText(item.updatedAt) || 'on an unknown date'}
                      {item.expiresAt ? ` · expires ${dayText(item.expiresAt)}` : ''}
                    </>
                  }
                />
                <CardBody>
                  <Field
                    label="Your answer"
                    aria-label={`Your answer to ${item.question}`}
                    value={text}
                    onChange={(e) =>
                      setDrafts((all) => ({
                        ...all,
                        [item.fingerprint]: { ...all[item.fingerprint], answerText: (e.target as HTMLInputElement).value },
                      }))
                    }
                  />
                  <Field
                    type="date"
                    label="Expires"
                    min={today}
                    aria-label={`Expiry for ${item.question}`}
                    value={expiry}
                    onChange={(e) =>
                      setDrafts((all) => ({
                        ...all,
                        [item.fingerprint]: { ...all[item.fingerprint], expiry: (e.target as HTMLInputElement).value },
                      }))
                    }
                  />
                </CardBody>
                <CardFooter>
                  <Button variant="ghost" size="sm" disabled={saving === item.fingerprint} onClick={() => remove(item)}>
                    Remove
                  </Button>
                  <Button
                    variant="gold"
                    size="sm"
                    disabled={saving === item.fingerprint || !dirty}
                    onClick={() => save(item)}
                  >
                    Save
                  </Button>
                </CardFooter>
              </Card>
            )
          })}
        </CardList>
      )}
      {message && (
        <Alert
          tone={message.bad ? 'danger' : 'success'}
          live={message.bad ? 'assertive' : 'polite'}
          data-testid="kn-answers-message"
          data-tone={message.bad ? 'bad' : 'ok'}
        >
          {message.text}
        </Alert>
      )}
    </div>
  )
}
